import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";
import { Warning as WarningIcon } from "@material-ui/icons";
import { Box } from "@material-ui/core";

export type APMISOnlineFormValues = {
  dateRequired: Date | null;
  dateSubmitted: Date | null;
  name: string;
  firstName: string;
  email: string;
  corpId: string;
  phoneNumber: string;
  country: string;
  managerEmail: string;
  department?: string;
  pairedStaffs?: string;
  role?: string;
  accessType1: boolean;
  accessType2: boolean;
  accessType3: boolean;
  report1: boolean;
  report2: boolean;
  report3: boolean;
  country1?: boolean;
  country2?: boolean;
  country3?: boolean;
  country4?: boolean;
  country5?: boolean;
  country6?: boolean;
  country7?: boolean;
  country8?: boolean;
  country9?: boolean;
  country10?: boolean;
  country11?: boolean;
  advance1?: boolean;
  advance2?: boolean;
  advance3?: boolean;
};

type Props = {
  onCancel: () => void;
  onSubmit: (values: APMISOnlineFormValues) => void;
  open: boolean;
  values: Partial<APMISOnlineFormValues>;
  readonly?: boolean;
};

export const APMISOnlineForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<APMISOnlineFormValues>({
    dateRequired: null,
    dateSubmitted: null,
    name: "",
    firstName: "",
    email: "",
    corpId: "",
    phoneNumber: "",
    country: "",
    managerEmail: "",
    accessType1: false,
    accessType2: false,
    accessType3: false,
    report1: false,
    report2: false,
    report3: false,
    country1: false,
    country2: false,
    country3: false,
    country4: false,
    country5: false,
    country6: false,
    country7: false,
    country8: false,
    country9: false,
    country10: false,
    country11: false,
    advance1: false,
    advance2: false,
    advance3: false,
    ...values,
  });

  function onFormValueChange(
    name: keyof APMISOnlineFormValues,
    value: any
  ): void {
    switch (name) {
      case "accessType1":
      case "accessType2":
      case "accessType3":
        form.set(
          Form.target.VALUES,
          Object.assign(
            {
              accessType1: false,
              accessType2: false,
              accessType3: false,
            },
            {
              [name]: value,
            }
          )
        );
        break;
      default:
        form.set(Form.target.VALUE, [name, value]);
        break;
    }
    validate(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validate(
    name: keyof APMISOnlineFormValues,
    value: APMISOnlineFormValues[typeof name]
  ): Promise<[keyof APMISOnlineFormValues, Error?]> {
    console.log("validate", name, value);
    switch (name) {
      case "dateRequired":
      case "dateSubmitted":
      case "name":
      case "firstName":
      case "email":
      case "corpId":
      case "phoneNumber":
      case "country":
      case "managerEmail":
        return Promise.resolve([name, Form.Validators.hasValue(value)]);
      case "accessType1":
      case "accessType2":
      case "accessType3": {
        const values = Object.assign(
          {
            accessType1: form.values.accessType1,
            accessType2: form.values.accessType2,
            accessType3: form.values.accessType3,
          },
          {
            [name]: value,
          }
        );

        if (Object.values(values).every((value) => value === false)) {
          return Promise.resolve([
            "accessType1",
            Form.Validators.hasValue(undefined),
          ]);
        } else {
          return Promise.resolve(["accessType1"]);
        }
      }
      case "report1":
      case "report2":
      case "report3": {
        const values = Object.assign(
          {
            report1: form.values.report1,
            report2: form.values.report2,
            report3: form.values.report3,
          },
          {
            [name]: value,
          }
        );

        if (Object.values(values).every((value) => value === false)) {
          return Promise.resolve([
            "report1",
            Form.Validators.hasValue(undefined),
          ]);
        } else {
          return Promise.resolve(["report1"]);
        }
      }
      case "country1":
      case "country2":
      case "country3":
      case "country4":
      case "country5":
      case "country6":
      case "country7":
      case "country8":
      case "country9":
      case "country10":
      case "country11": {
        const values = Object.assign(
          {
            country1: form.values.country1,
            country2: form.values.country2,
            country3: form.values.country3,
            country4: form.values.country4,
            country5: form.values.country5,
            country6: form.values.country6,
            country7: form.values.country7,
            country8: form.values.country8,
            country9: form.values.country9,
            country10: form.values.country10,
            country11: form.values.country11,
          },
          {
            [name]: value,
          }
        );

        if (Object.values(values).every((value) => value === false)) {
          return Promise.resolve([
            "country1",
            Form.Validators.hasValue(undefined),
          ]);
        } else {
          return Promise.resolve(["country1"]);
        }
      }
      default:
        return Promise.resolve([name]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validate(name as keyof APMISOnlineFormValues, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);

      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      size="md"
      title="GLOBAL PAYMENTS ASIA PACIFIC - AP MIS Online Account Form"
      readonly={readonly}
    >
      <Form>
        <Form.Text color="warning.main">
          <Box display="flex">
            <WarningIcon />
            <Box pl={1}>
              Please complete the following and email to your manager for
              approval and your manager will send the approved form to “AP
              Access Request”. AP Access Request will only process the forms
              sent from the relevant Country Manager if permissions in “Advanced
              Permission” section is/are selected. Submitted forms requires 10
              business days for account creation.
            </Box>
          </Box>
        </Form.Text>
        <Form.Title>1 - User Information</Form.Title>
        <Form.Datebox
          error={form.errors.dateSubmitted}
          label="Date Submitted"
          name="dateSubmitted"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.dateSubmitted}
        />
        <Form.Textbox
          error={form.errors.phoneNumber}
          label="Phone Number"
          name="phoneNumber"
          onChange={onFormValueChange}
          required
          size={6}
          type="tel"
          value={form.values.phoneNumber}
        />
        <Form.Datebox
          error={form.errors.dateRequired}
          label="Date Required"
          name="dateRequired"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.dateRequired}
        />
        <Form.Textbox
          error={form.errors.country}
          label="Country"
          name="country"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.country}
        />
        <Form.Textbox
          error={form.errors.name}
          label="Staff Last Name"
          name="name"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.name}
        />
        <Form.Textbox
          error={form.errors.managerEmail}
          label="Manager Email Address"
          name="managerEmail"
          onChange={onFormValueChange}
          required
          size={6}
          type="email"
          value={form.values.managerEmail}
        />
        <Form.Textbox
          error={form.errors.firstName}
          label="Staff First Name"
          name="firstName"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.firstName}
        />
        <Form.Textbox
          error={form.errors.department}
          label="Department (optional)"
          name="department"
          onChange={onFormValueChange}
          size={6}
          value={form.values.department}
        />
        <Form.Textbox
          error={form.errors.email}
          label="Email"
          name="email"
          onChange={onFormValueChange}
          required
          size={6}
          type="email"
          value={form.values.email}
        />
        <Form.Textbox
          error={form.errors.pairedStaffs}
          label="Paired Staffs (optional)"
          name="pairedStaffs"
          onChange={onFormValueChange}
          size={6}
          value={form.values.pairedStaffs}
        />
        <Form.Textbox
          error={form.errors.corpId}
          label="CORP Login ID"
          name="corpId"
          onChange={onFormValueChange}
          required
          size={6}
          value={form.values.corpId}
        />
        <Form.Textbox
          error={form.errors.role}
          label="Role (optional)"
          name="role"
          onChange={onFormValueChange}
          size={6}
          value={form.values.role}
        />
        <Form.Title error={form.errors.accessType1}>2 - Access Type</Form.Title>
        <Form.Radiobutton
          label="New"
          name="accessType1"
          onChange={onFormValueChange}
          size={3}
          value={form.values.accessType1}
        />
        <Form.Radiobutton
          label="Additional Permission"
          name="accessType2"
          onChange={onFormValueChange}
          size={3}
          value={form.values.accessType2}
        />
        <Form.Radiobutton
          label="Delete Account"
          name="accessType3"
          onChange={onFormValueChange}
          size={3}
          value={form.values.accessType3}
        />
        <Form.Title error={form.errors.report1}>
          3 - Reports and Functions
        </Form.Title>
        <Form.Checkbox
          label="Top 'N' Merchant Performance (Long &amp; Short)"
          name="report1"
          onChange={onFormValueChange}
          size={4}
          value={form.values.report1}
        />
        <Form.Checkbox
          label="Revenue History Report"
          name="report2"
          onChange={onFormValueChange}
          size={4}
          value={form.values.report2}
        />
        <Form.Checkbox
          label="Merchant Profile Enquiry"
          name="report3"
          onChange={onFormValueChange}
          size={4}
          value={form.values.report3}
        />
        <Form.Title error={form.errors.country1}>
          4 - Country Permission
        </Form.Title>
        <Form.Checkbox
          label="Hong Kong"
          name="country1"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country1}
        />
        <Form.Checkbox
          label="China"
          name="country4"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country4}
        />
        <Form.Checkbox
          label="Malaysia"
          name="country6"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country6}
        />
        <Form.Checkbox
          label="Singapore"
          name="country8"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country8}
        />
        <Form.Checkbox
          label="Taiwan"
          name="country10"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country10}
        />
        <Form.Checkbox
          label="Macau"
          name="country2"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country2}
        />
        <Form.Checkbox
          label="India"
          name="country5"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country5}
        />
        <Form.Checkbox
          label="Maldives"
          name="country7"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country7}
        />
        <Form.Checkbox
          label="Sri Lanka"
          name="country9"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country9}
        />
        <Form.Checkbox
          label="Philippines"
          name="country11"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country11}
        />
        <Form.Checkbox
          label="Brunei"
          name="country3"
          onChange={onFormValueChange}
          size={3}
          value={form.values.country3}
        />
        <Form.Title subtitle="This section requires approval from the Country Manager">
          5 - Advanced permission
        </Form.Title>
        <Form.Checkbox
          label="Download / Export"
          name="advance1"
          onChange={onFormValueChange}
          size={12}
          value={form.values.advance1}
        />
        <Form.Checkbox
          label="Sensitive Data Display (Interchange / NDMI / Disccount)"
          name="advance2"
          onChange={onFormValueChange}
          size={12}
          value={form.values.advance2}
        />
        <Form.Checkbox
          label="Industry Performance Report"
          name="advance3"
          onChange={onFormValueChange}
          size={12}
          value={form.values.advance3}
        />
      </Form>
    </Wrapper>
  );
};
